import { Injectable } from '@angular/core';
import { SQLite, SQLiteObject } from '@ionic-native/sqlite/ngx';
import { Platform } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { Network } from '@ionic-native/network/ngx';
import { environment } from 'src/environments/environment';
import { CoreProvider } from 'src/app/providers/core/core.service';
import * as localforage from 'localforage';

@Injectable({
  providedIn: 'root'
})
export class ErrorLoggerService {

  private db: SQLiteObject | null = null;
  private isBrowserPlatform = false;
  private isSyncing = false;
  private lastSync = 0;
  private recentErrors = new Map<string, number>();
  private readonly DEDUPE_WINDOW = 5000;

  constructor(
    private sqlite: SQLite,
    private platform: Platform,
    private http: HttpClient,
    private network: Network,
    private coreService: CoreProvider
  ) {
    this.detectPlatform();
    this.platform.ready().then(async () => {
      await this.initDB();
       // cleanup old synced logs on mobile
      if (!this.isBrowserPlatform) {
        await this.cleanupOldSyncedLogs(30);
      }
      this.startSyncListener();
      await this.coreService.deviceId();
    });
    this.platform.resume.subscribe(() => this.syncErrors());
  }

  private detectPlatform() {
    this.isBrowserPlatform =
      this.platform.is('desktop') ||
      this.platform.is('mobileweb') ||
      this.platform.is('pwa') ||
      !this.platform.is('cordova');
  }

  async initDB() {
    if (this.isBrowserPlatform) {
      localforage.config({ name: "AppLogsDB", storeName: "errorlogs" });
      return;
    }
    try {
      this.db = await this.sqlite.create({ name: 'app_logs.db', location: 'default' });
      await this.db.executeSql(
        `CREATE TABLE IF NOT EXISTS ErrorLogs(
          Id INTEGER PRIMARY KEY AUTOINCREMENT,
          ErrorType TEXT,
          Status INTEGER,
          ErrorMessage TEXT,
          Url TEXT,
          Method TEXT,
          Route TEXT,
          RawResponse TEXT,
          Timestamp TEXT,
          DeviceId TEXT,
          UserId TEXT,
          Synced INTEGER DEFAULT 0
        )`, []
      );
    } catch (err) {
      console.error('SQLite Init Error', err);
    }
  }

  private isDuplicate(error: any): boolean {
    const key = `${error.ErrorType}|${error.Url}|${error.Status}`;
    const now = Date.now();
    const last = this.recentErrors.get(key);

    if (last && now - last < this.DEDUPE_WINDOW) {
      return true;
    }

    this.recentErrors.set(key, now);
    return false;
  }

  async log(error: any) {
    if (!this.isBrowserPlatform && !this.db) return;
    if (this.isDuplicate(error)) return;
    const logEntry = {
      ErrorType: error.ErrorType || '',
      Status: error.Status || null,
      ErrorMessage: error.ErrorMessage || '',
      Url: error.Url || '',
      Method: error.Method || '',
      Route: error.Route || '',
      RawResponse: error.RawResponse || '',
      Timestamp: new Date().toISOString(),
      DeviceId: await this.coreService.deviceId() || '',
      UserId: this.coreService.getUserId() || '',
      Synced: 0
    };

    // Browser platform
    if (this.isBrowserPlatform) {
      try {
        await localforage.setItem(Date.now().toString(), logEntry);
        console.log('Saved to IndexedDB');
      } catch (err) {
        console.error('IndexedDB Log Error:', err);
        this.sendDirectlyToAPI([logEntry]);
      }
      return;
    }

    // Device platform
    try {
      await this.db!.executeSql(
        `INSERT INTO ErrorLogs 
        (ErrorType, Status, ErrorMessage, Url, Method, Route, RawResponse, Timestamp, DeviceId, UserId)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          logEntry.ErrorType,
          logEntry.Status,
          logEntry.ErrorMessage,
          logEntry.Url,
          logEntry.Method,
          logEntry.Route,
          JSON.stringify(logEntry.RawResponse),
          logEntry.Timestamp,
          logEntry.DeviceId|| await this.coreService.deviceId(),
          logEntry.UserId
        ]
      );
    } catch (err) {
      console.error('SQLite Insert Error', err);
      this.sendDirectlyToAPI([logEntry]);
    }
  }

  startSyncListener() {
    this.network.onDisconnect().subscribe(() => {
      this.log({
        ErrorType: 'NetworkError',
        Status: 0,
        ErrorMessage: 'Poor or lost network connection',
        Url: window.location.href,
        Method: '',
        Route: '',
        RawResponse: 'Network disconnected'
      });
    });

    this.network.onConnect().subscribe(() => {
      const now = Date.now();
      if (now - this.lastSync < 5000) return;  
      this.lastSync = now;

      setTimeout(() => this.syncErrors(), 2000);
    });
  }

  async syncErrors() {
    if (!environment.errorLogging) return false;
    if (this.isSyncing) return false;
    this.isSyncing = true;
    try {
      // ---------- BROWSER MODE ----------
      if (this.isBrowserPlatform) {
        const keys = await localforage.keys();
        if (!keys.length) return false;

        const rows = [];

        for (const k of keys) {
          const item = await localforage.getItem(k);
          if (item) rows.push(item);
        }

        const success = await this.sendDirectlyToAPI(rows);

        if (success) {
          for (const k of keys) await localforage.removeItem(k);
        }

        return success;
      }

      // ---------- DEVICE MODE ----------
      if (!this.db || this.network.type === 'none') return false;

      const result = await this.db.executeSql(
        `SELECT * FROM ErrorLogs WHERE Synced = 0 ORDER BY Timestamp LIMIT 20`,
        []
      );

      if (result.rows.length === 0) return false;

      const rows = [];

      for (let i = 0; i < result.rows.length; i++) {
        rows.push(result.rows.item(i));
      }

      const success = await this.sendDirectlyToAPI(rows);
      if (success) {
        const ids = rows.map(r => r.Id);
        const placeholders = ids.map(() => '?').join(',');
        await this.db.executeSql(`UPDATE ErrorLogs SET Synced = 1 WHERE Id IN (${placeholders})`, ids);
        await this.cleanupOldSyncedLogs(30);
      }

      return success;
    } catch (err) {
      console.error('Sync Error', err);
      return false;
    } finally {
      this.isSyncing = false;  // ALWAYS RESET
    }
  }

  private async cleanupOldSyncedLogs(days = 30): Promise<void> {
    if (!this.db) return;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    try {
      await this.db.executeSql(
        `DELETE FROM ErrorLogs
        WHERE Synced = 1 AND Timestamp < ?`,
        [cutoff.toISOString()]
      );
    } catch (err) {
      console.error('SQLite Cleanup Error', err);
    }
  }

  private async sendDirectlyToAPI(logs: any[]): Promise<boolean> {
    if (!logs.length) return false;

    const batchSize = 20;

  try {
    const deviceId = await this.coreService.deviceId();
    for (let i = 0; i < logs.length; i += batchSize) {
      const batch = logs.slice(i, i + batchSize);
      const payload = batch.map(log => ({
        ErrorType: log.ErrorType,
        Status: log.Status,
        ErrorMessage: log.ErrorMessage,
        Url: log.Url,
        Method: log.Method,
        Route: log.Route,
        RawResponse: typeof log.RawResponse === 'string' ? log.RawResponse : JSON.stringify(log.RawResponse),
        TimestampUtc: log.Timestamp,
        DeviceId: log.DeviceId || deviceId,
        UserId: log.UserId
      }));
      await this.http.post(
          `${environment.apiUrl}error/add-error-logs`,
          payload
      ).toPromise();
      }
      return true;
    } catch (err) {
      console.error('API Send Error', err);
      return false;
    }
  }
}
